import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { UI_OPERATION_STATUS_REQUEST } from '@uncinc/react-drupal-core';

import {
  FormikWrapper,
  FormTextarea as Textarea,
  FormTextField as TextField,
} from '@uncinc/uncinc-react-kitchen-sink';

import useClasses from '../../../../hooks/useClasses';
import useOperationStatus from '../../../../hooks/useOperationStatus';
import Button from '../../../Button';

import './style.scss';

interface InitialValues {
  title: string;
  body: string;
}

interface Props {
  initialValues: InitialValues;
  onSubmit: (values: InitialValues) => void;
  showDeleteButton?: boolean;
  onDelete?: () => void;
}

const ForumPost: React.FC<Props> = ({
  initialValues,
  onSubmit,
  showDeleteButton,
  onDelete,
}) => {
  const { t } = useTranslation();

  const { buildClass } = useClasses('ForumPost');
  const operationStatus = useOperationStatus('node', 'add');

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .trim()
      .required(t('pages.forum.form.fields.title.validation.required')),
    body: Yup.string()
      .trim()
      .required(t('pages.forum.form.fields.body.validation.required')),
  });

  return (
    <div className={buildClass()}>
      <FormikWrapper
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={(values: InitialValues, { setSubmitting }) => {
          onSubmit(values);
          setSubmitting(false);
        }}
      >
        <TextField
          fieldName="title"
          title={t('pages.forum.form.fields.title.label')}
          value={initialValues.title}
          placeholder={t('pages.forum.form.fields.title.placeholder')}
        />
        <Textarea
          fieldName="body"
          title={t('pages.forum.form.fields.body.label')}
          value={initialValues.body}
          placeholder={t('pages.forum.form.fields.body.placeholder')}
        />

        <div className={buildClass('buttons')}>
          <Button htmlType="submit" buttonState={operationStatus === UI_OPERATION_STATUS_REQUEST && 'disabled'}>
            {t('pages.forum.form.submit')}
          </Button>

          {showDeleteButton && (
            <Button htmlType="button" onClick={() => onDelete()}>
              {t('pages.forum.edit.delete')}
            </Button>
          )}
        </div>
      </FormikWrapper>
    </div>
  );
};

export default ForumPost;
